/* eslint-disable react/no-danger */
/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Lightbox from 'react-images';
import useStyles from 'isomorphic-style-loader/useStyles';
import ReactTimeAgo from 'react-time-ago';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// MaterialUI
import Avatar from '@material-ui/core/Avatar';
import Collapse from '@material-ui/core/Collapse';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import Rating from '@material-ui/lab/Rating';

// app: components
import CommentActions from 'components/Comments/CommentActions';
import InlineCommentEditor from 'components/Comments/Comment/InlineCommentEditor';
import Link from 'components/Link';

// app modules: services
import assets from 'services/assets';
import route from 'services/route';

// app: props
import CommentProps from './CommentProps';

// app: constants
import { TRUNCATION_LIMIT } from '../../../constants';

// assets
import CssComment from './Comment.scss';

const Comment = React.forwardRef((props, ref) => {
  useStyles(CssComment);
  const {
    children,
    comment,
    focused,
    level,
    onClick,
    parentComment,
    responding,
    selected,
    withModeratorControls,
    isModerator,
    hasPrimaryImage,
  } = props;
  const {
    as_author,
    created_at,
    id,
    moderation_status,
    parent_id,
    safe_html,
    star_rating,
    image_urls,
    type,
    updated_at,
    user,
    country_name,
  } = comment;

  const [inlineEditMode, setInlineEditMode] = useState(false);
  const [responseFormIsOpen, setResponseFormIsOpen] = useState(false);
  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);

  const displayType = useSelector(state => state.dimensions.displayType);
  const isMyComment = useSelector(state => state.user.id === user.id);
  const loggedIn = useSelector(state => state.user.logged_in);
  const wasEdited = created_at !== updated_at;

  const handleInlineEdit = () => {
    setInlineEditMode(!inlineEditMode);
  };

  const handleInlineEditFormClose = () => {
    setInlineEditMode(false);
  };

  const handleCloseLightBox = () => {
    setIsLightBoxOpen(false);
  };

  const lightboxImage = useMemo(
    () => {
      if (!image_urls) {
        return null;
      }

      return {
        src: image_urls[1],
        srcSet: image_urls[1],
        thumbnail: image_urls[0],
      };
    },
    [image_urls],
  );

  let usingCollapse = false;
  let commentBody;

  const renderCommentText = () => {
    if (
      (displayType === 'mobile' && safe_html.length > TRUNCATION_LIMIT.MOBILE) ||
      (displayType === 'tablet' && safe_html.length > TRUNCATION_LIMIT.TABLET) ||
      (displayType === 'desktop' && safe_html.length > TRUNCATION_LIMIT.DESKTOP)
    ) {
      usingCollapse = true;
      return (
        <Collapse
          in={selected}
          collapsedHeight="4.55rem"
          className={classNames('comment__collapse', { 'comment__collapse--closed': !selected })}
        >
          <div
            className="comment__content__text"
            dangerouslySetInnerHTML={{
              __html: safe_html,
            }}
          />
        </Collapse>
      );
    }

    return (
      <div
        className="comment__content__text"
        dangerouslySetInnerHTML={{
          __html: safe_html,
        }}
      />
    );
  };

  if (inlineEditMode) {
    commentBody = (
      <InlineCommentEditor
        commentId={comment.id}
        isModerator={isModerator}
        onClose={handleInlineEditFormClose}
      />
    );
  } else if (safe_html) {
    commentBody = (
      <div className="comment__content">
        {renderCommentText()}
        {lightboxImage && (
          <div className="comment__image__lightbox">
            <Lightbox
              backdropClosesModal
              images={[lightboxImage]}
              isOpen={isLightBoxOpen}
              onClose={handleCloseLightBox}
              showImageCount={false}
              theme={{ image: { userSelect: 'none', pointerEvents: 'none' } }}
            />
            <button
              type="button"
              onClick={() => setIsLightBoxOpen(true)}
              className="comment__image__lightbox__thumbnail"
            >
              <img src={lightboxImage.thumbnail} alt="" />
            </button>
          </div>
        )}
      </div>
    );
  }

  const avatar = (
    <Avatar
      className="comment__avatar"
      src={assets.avatarUrl(user.avatar || 'default', {
        resize: 'fit-in',
        width: 80,
        height: 80,
      })}
    />
  );

  const className = classNames('comment', `comment--${type}`, {
    'comment--focused': focused || inlineEditMode,
    'comment--removed': ['removed', 'auto_remove'].includes(moderation_status),
    'comment--clickable': usingCollapse,
    'comment--response': level > 0,
    'comment--with-responses': children?.length,
    'comment--answer': parentComment?.type === 'question',
  });

  const canIReply = useMemo(
    () => {
      if (!isMyComment) {
        return level === 0 && !children?.length;
      }

      return level > 0 && children && children?.length;
    },
    [isMyComment, children, level],
  );

  if (type === 'question') {
    return (
      <React.Fragment>
        <div
          className={className}
          id={parent_id ? `${type}${parent_id}-${id}` : `${type}${id}`}
          ref={ref}
          onClick={event => onClick(event, comment)}
          onKeyPress={event => onClick(event, comment)}
          tabIndex={usingCollapse ? 0 : null}
          role="button"
        >
          <div className="comment__header-row">
            <QuestionAnswerIcon color="primary" />
            {user ? (
              <React.Fragment>
                <div className="comment__question-info comment__header__item">
                  Question from{' '}
                  <strong>
                    {user.author_id ? (
                      <Link to={route.person(user.author_id, true)}>{user.display_name}</Link>
                    ) : (
                      user.display_name
                    )}
                  </strong>
                </div>
                {as_author ? <div className="comment__badge">Author</div> : null}
                {isMyComment && (
                  <button aria-label="edit-question" className="link link--energized edit_button">
                    Edit this question
                  </button>
                )}
              </React.Fragment>
            ) : null}
          </div>
          <div className="comment__main">{commentBody}</div>
          {inlineEditMode ? null : (
            <CommentActions
              comment={comment}
              hasPrimaryImage={hasPrimaryImage}
              canRespond={canIReply}
              isModerator={isModerator}
              responding={level === 0 && responding}
              hideQuestionText={isMyComment && children && children?.length}
              withModeratorControls={withModeratorControls}
              onEdit={handleInlineEdit}
            />
          )}
        </div>
        {children}
        {inlineEditMode || type === 'response' || !children?.length ? null : (
          <CommentActions
            comment={comment}
            hasPrimaryImage={hasPrimaryImage}
            canRespond
            isModerator={isModerator}
            className={children?.length ? 'comment--response' : ''}
            responding={responding}
            withModeratorControls={false}
            onEdit={handleInlineEdit}
            hideQuestionText
          />
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div
        className={className}
        id={parent_id ? `${type}${parent_id}-${id}` : `${type}${id}`}
        ref={ref}
        onClick={event => onClick(event, comment)}
        onKeyPress={event => onClick(event, comment)}
        tabIndex={usingCollapse ? 0 : null}
        role="button"
      >
        {user ? (
          <div className="comment__top">
            {user.author_id ? (
              <Link to={route.person(user.author_id, true)}>{avatar}</Link>
            ) : (
              avatar
            )}
            <div className="comment__header">
              <div className="comment__header-row">
                {user ? (
                  <React.Fragment>
                    <div className="comment__header__item">
                      <strong>
                        {user.author_id ? (
                          <Link to={route.person(user.author_id, true)}>{user.display_name}</Link>
                        ) : (
                          user.display_name
                        )}
                      </strong>
                    </div>
                    {as_author ? <div className="comment__badge">Author</div> : null}
                  </React.Fragment>
                ) : null}
              </div>
              <div className="comment__header-row">
                {star_rating ? (
                  <div className="comment__header__item comment__stars">
                    <Rating
                      value={star_rating}
                      readOnly
                      size="small"
                      classes={{
                        root: 'comment__star',
                      }}
                    />
                  </div>
                ) : null}
                {created_at ? (
                  <div className="comment__header__item comment__created comment__time">
                    {wasEdited ? <span>updated&nbsp;</span> : null}
                    <ReactTimeAgo date={new Date(updated_at)} locale="en-US" />
                    {country_name ? ` from ${country_name}` : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className="comment__main">
          {commentBody}
          {inlineEditMode ? null : (
            <CommentActions
              comment={comment}
              hasPrimaryImage={hasPrimaryImage}
              className="margin-top-10"
              canRespond={canIReply}
              isModerator={isModerator}
              responding={level === 0 && responding}
              withModeratorControls={withModeratorControls}
              onEdit={handleInlineEdit}
            />
          )}
        </div>
      </div>
      {children
        ? children.map((child, i) => (
            <div className={i === children.length - 1 && !loggedIn ? 'comment--end-of-thread' : ''}>
              {child}
            </div>
          ))
        : null}
      {inlineEditMode || type === 'response' || !children?.length ? null : (
        <CommentActions
          comment={comment}
          hasPrimaryImage={hasPrimaryImage}
          canRespond
          isModerator={isModerator}
          responding={responding}
          withModeratorControls={false}
          onEdit={handleInlineEdit}
          className={
            responseFormIsOpen
              ? 'comment__response-link comment__response-link--form-open'
              : 'comment__response-link'
          }
          onResponseFormOpen={() => setResponseFormIsOpen(true)}
          onResponseFormClose={() => setResponseFormIsOpen(false)}
        />
      )}
    </React.Fragment>
  );
});

Comment.displayName = 'Comment';

Comment.propTypes = {
  comment: CommentProps.isRequired,
  focused: PropTypes.bool,
  hasPrimaryImage: PropTypes.bool,
  level: PropTypes.number,
  onClick: PropTypes.func,
  parentComment: CommentProps,
  selected: PropTypes.bool,
  isModerator: PropTypes.bool,
  withModeratorControls: PropTypes.bool,
};

Comment.defaultProps = {
  focused: false,
  hasPrimaryImage: false,
  level: 0,
  onClick: () => {},
  parentComment: null,
  selected: false,
  isModerator: false,
  withModeratorControls: false,
};

Comment.whyDidYouRender = false;

export default React.memo(Comment);
